/**
 * SolutionShowcase — Welcome-page chatbot before/after proof (Phase 14.2 FE).
 *
 *  Enhanced: the comparison now plays as a short sequence when it scrolls into
 *  view (question → typing → dead-end, then the same question → typing → three
 *  checks verifying one by one → receipt → resolved), with a Replay control.
 *  Reduced motion and no-JS show the finished state. `Card` / `CARDS` exports
 *  are unchanged; the card art got square geometry and a little ambient motion.
 *
 *  A. Chat-bubble before/after — "your chatbot can talk; this is what happens
 *     when it can act." The same customer question answered by a generic bot
 *     (dead-ends) vs. Ontoligent underneath (verifies, acts, resolves).
 * Adapted to the app design system (scoped `.proto-fx`, HSL tokens, the shared
 * `.section-head`/`.section-eyebrow`/`.section-sub` rhythm from `.welcome-fx`),
 * NOT a copy of the raw draft. CTAs route to the corporate-email-gated showcase
 * (the decided email-collection surface) — configurable via VITE_SHOWCASE_URL.
 */
import { ArrowRight, Check, RotateCcw } from "lucide-react";
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { SHOWCASE_BASE } from "@/lib/showcase";

const BEFORE_MSGS = [
  { who: "them", text: "I had a fender bender, what happens now?" },
  {
    who: "me",
    text: "Here\u2019s your claim number. An adjuster will review your case within 3\u20135 business days.",
  },
];

const AFTER_ACTIONS = [
  { label: "Policy verified & active", meta: "POLICY / P-20481", detail: "Coverage, effective date and claimant identity resolved against the approved policy graph." },
  { label: "Damage within auto-approval limits", meta: "CONTROL / CLM-07", detail: "Estimated exposure passes the configured threshold; exceptions still route to human review." },
  { label: "Approved repairer & tow booked", meta: "ACTION / ACT-193", detail: "Two governed write-backs complete with connector receipts and reversible transaction IDs." },
];

export type Card = {
  idx: string;
  slug: string;
  title: string;
  body: string;
  statNum: string;
  statCap: string;
  art: ReactNode;
};

export const CARDS: Card[] = [
  {
    idx: "01",
    slug: "chatbot-amplifier",
    title: "Call & chat, made to act",
    body: "The same assistant your customers already talk to \u2014 now able to check coverage, resolve straightforward requests, and hand off complex ones with full context instead of a blank slate.",
    statNum: "\u2191",
    statCap: "Rising strain on call-center capacity as digital inquiry volume grows.",
    art: (
      <svg viewBox="0 0 180 108" width="154" height="92" aria-hidden="true">
        <defs>
          <filter id="paS1" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="2.2" floodColor="#0b1220" floodOpacity="0.10" />
          </filter>
        </defs>
        <g filter="url(#paS1)"><rect className="pa-panel" x="14" y="24" width="66" height="60" rx="2" /></g>
        <rect className="pa-bubble-in" x="24" y="34" width="32" height="9" rx="4.5" />
        <rect className="pa-bubble-in" x="24" y="47" width="46" height="9" rx="4.5" />
        <rect className="pa-bubble-acc" x="38" y="62" width="34" height="10" rx="5" />
        <path className="pa-accent-dash" d="M84 54 H110" />
        <path className="pa-accent" d="M104 48 L112 54 L104 60" />
        <g filter="url(#paS1)"><rect className="pa-accent-fill" x="114" y="20" width="52" height="68" rx="2" /></g>
        <path className="pa-accent" d="M128 38 L152 38 M128 38 L140 56 M152 38 L140 56" />
        <circle className="pa-node-acc" cx="128" cy="38" r="4.5" />
        <circle className="pa-node-acc" cx="152" cy="38" r="4.5" />
        <circle className="pa-node-acc" cx="140" cy="56" r="4.5" />
        <circle className="pa-good-fill" cx="140" cy="73" r="8.5" />
        <path className="pa-check" d="M136 73 l3 3 l5.5 -6.5" />
      </svg>
    ),
  },
  {
    idx: "02",
    slug: "underwriting-amplifier",
    title: "Underwriting, without the tab-switching",
    body: "One governed view of a submission instead of a dozen open tabs \u2014 routine files clear themselves, and your underwriters spend judgment only on the ones that actually need it.",
    statNum: "12+",
    statCap: "Systems underwriters often juggle per submission, per industry reporting.",
    art: (
      <svg viewBox="0 0 180 108" width="154" height="92" aria-hidden="true">
        <defs>
          <filter id="paS2" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="2.2" floodColor="#0b1220" floodOpacity="0.10" />
          </filter>
        </defs>
        <g filter="url(#paS2)"><rect className="pa-panel" x="12" y="30" width="46" height="54" rx="0" transform="rotate(-9 35 57)" /></g>
        <g filter="url(#paS2)"><rect className="pa-panel" x="26" y="22" width="46" height="54" rx="0" transform="rotate(6 49 49)" /></g>
        <g filter="url(#paS2)"><rect className="pa-panel" x="18" y="32" width="46" height="54" rx="0" transform="rotate(-2 41 59)" /></g>
        <g transform="rotate(-2 41 59)">
          <rect className="pa-accent-soft" x="26" y="40" width="30" height="6" rx="3" />
          <rect className="pa-line-fill" x="26" y="52" width="30" height="4" rx="2" />
          <rect className="pa-line-fill" x="26" y="61" width="22" height="4" rx="2" />
        </g>
        <path className="pa-accent-dash" d="M80 56 H106" />
        <path className="pa-accent" d="M100 50 L108 56 L100 62" />
        <g filter="url(#paS2)"><rect className="pa-accent-fill" x="112" y="24" width="54" height="60" rx="2" /></g>
        <rect className="pa-accent-soft" x="120" y="32" width="28" height="6" rx="3" />
        <circle className="pa-good-fill" cx="125" cy="50" r="5" /><path className="pa-check" d="M122.5 50 l2 2 l3.5 -4" />
        <rect className="pa-line-fill" x="134" y="47.5" width="24" height="4" rx="2" />
        <circle className="pa-good-fill" cx="125" cy="62" r="5" /><path className="pa-check" d="M122.5 62 l2 2 l3.5 -4" />
        <rect className="pa-line-fill" x="134" y="59.5" width="24" height="4" rx="2" />
        <circle className="pa-good-fill" cx="125" cy="74" r="5" /><path className="pa-check" d="M122.5 74 l2 2 l3.5 -4" />
        <rect className="pa-line-fill" x="134" y="71.5" width="18" height="4" rx="2" />
      </svg>
    ),
  },
  {
    idx: "03",
    slug: "siu-graph-investigator",
    title: "See the fraud ring, not just the case",
    body: "The shared repair shop. The reused address. The claim from three years ago. Investigators follow the connections directly instead of reconstructing them by hand.",
    statNum: "3",
    statCap: "Hops is often all it takes to surface a hidden pattern across claims.",
    art: (
      <svg viewBox="0 0 180 108" width="154" height="92" aria-hidden="true">
        <defs>
          <filter id="paS3" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#0b1220" floodOpacity="0.10" />
          </filter>
        </defs>
        <path className="pa-line" d="M40 32 L92 56" />
        <path className="pa-line" d="M38 78 L92 56" />
        <path className="pa-line" d="M92 56 L150 74" />
        <path className="pa-flag-edge" d="M92 56 L142 32" />
        <g filter="url(#paS3)">
          <circle className="pa-node-fill" cx="40" cy="32" r="8.5" />
          <circle className="pa-node-fill" cx="38" cy="78" r="8.5" />
          <circle className="pa-node-fill" cx="150" cy="74" r="7.5" />
          <circle className="pa-accent-node" cx="92" cy="56" r="11" />
        </g>
        <circle className="pa-ring" cx="142" cy="32" r="14" />
        <g filter="url(#paS3)"><circle className="pa-flag-fill" cx="142" cy="32" r="9.5" /></g>
        <path className="pa-flag-mark" d="M142 28 v4.5" /><circle className="pa-flag-dot" cx="142" cy="35.5" r="1" />
      </svg>
    ),
  },
];

type Reveal = "idle" | "armed" | "seen";

/**
 * Starts the entrance once the element is mostly on screen.
 * "idle" (server render / no JS / reduced motion) shows the finished state.
 */
function useReveal<T extends HTMLElement>(threshold = 0.35) {
  const ref = useRef<T>(null);
  const [state, setState] = useState<Reveal>("idle");
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!("IntersectionObserver" in window) || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setState("seen");
      return;
    }
    setState("armed");
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setState("seen");
          observer.disconnect();
        }
      },
      { threshold },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, state] as const;
}

/** Playback timeline in seconds. Each beat waits for the one before it. */
const T = {
  question: 0.15,
  typingBefore: 0.7,
  answerBefore: 1.6,
  deadEnd: 2.4,
  arrow: 2.7,
  questionAfter: 3.1,
  typingAfter: 3.7,
  answerAfter: 4.6,
  actions: [5.3, 6.1, 6.9],
  detail: 7.7,
  resolved: 8.1,
};
const at = (seconds: number) => ({ "--d": `${seconds}s` }) as CSSProperties;

function Typing({ delay }: { delay: number }) {
  return (
    <div className="proto-bubble proto-typing" aria-hidden="true" style={at(delay)}>
      <i />
      <i />
      <i />
    </div>
  );
}

export default function SolutionShowcase() {
  const [activeAction, setActiveAction] = useState(0);
  const [run, setRun] = useState(0);
  const [stageRef, reveal] = useReveal<HTMLDivElement>();

  const replay = () => {
    setActiveAction(0);
    setRun((current) => current + 1);
  };

  return (
    <>
      {/* SECTION A — chat-bubble before/after */}
      <section id="amplify" className="proto-fx">
        <div className="container">
          <div className="section-head">
            <div>
              <div className="section-eyebrow">Your AI, upgraded</div>
              <h2>
                Your chatbot can talk.
                <br />
                This is what happens when it can <em>act</em>.
              </h2>
            </div>
            <p className="section-sub">
              You don&rsquo;t have to replace the assistant your team already uses. Point it at a governed knowledge
              graph instead of a folder of PDFs, and the same question gets a different kind of answer.
            </p>
          </div>

          <div className="proto-stage" ref={stageRef} data-reveal={reveal}>
            <div className="proto-spine">
              <span className="proto-spine-label">Same customer &middot; same question</span>
              <span className="proto-spine-line" />
              {reveal === "seen" && (
                <button type="button" className="proto-replay" onClick={replay}>
                  <RotateCcw size={12} aria-hidden /> Replay
                </button>
              )}
            </div>

            <div className="proto-ba" key={run}>
              <div className="proto-col before" role="group" aria-label="Today, a chatbot">
                <div className="proto-col-tag">Today &mdash; a chatbot</div>
                {BEFORE_MSGS.map((m, i) => (
                  <div
                    key={i}
                    className={`proto-row proto-anim ${m.who === "me" ? "mine" : ""}`}
                    style={at(m.who === "me" ? T.answerBefore : T.question)}
                  >
                    <span className="proto-av">{m.who === "me" ? "AI" : "C"}</span>
                    <div className="proto-bubble">{m.text}</div>
                    {m.who === "me" && <Typing delay={T.typingBefore} />}
                  </div>
                ))}
                <div className="proto-deadend proto-anim" style={at(T.deadEnd)}>
                  Conversation ends here <span className="proto-dots">&middot;&middot;&middot;</span>
                </div>
              </div>

              <div className="proto-mid">
                <span className="proto-arrow" style={at(T.arrow)}>
                  <ArrowRight size={16} aria-hidden />
                </span>
              </div>

              <div className="proto-col after" role="group" aria-label="With Ontoligent underneath" style={at(T.questionAfter - 0.4)}>
                <div className="proto-col-tag good">With Ontoligent underneath</div>
                <div className="proto-row proto-anim" style={at(T.questionAfter)}>
                  <span className="proto-av">C</span>
                  <div className="proto-bubble">I had a fender bender, what happens now?</div>
                </div>
                <div className="proto-row mine proto-anim" style={at(T.answerAfter)}>
                  <span className="proto-av on">AI</span>
                  <div className="proto-bubble on">Let me take care of that for you right now.</div>
                  <Typing delay={T.typingAfter} />
                </div>
                <div className="proto-actions">
                  {AFTER_ACTIONS.map((a, index) => (
                    <button
                      key={a.label}
                      type="button"
                      className={`proto-act proto-anim ${activeAction === index ? "active" : ""}`}
                      style={at(T.actions[index])}
                      onClick={() => setActiveAction(index)}
                      aria-pressed={activeAction === index}
                    >
                      <span className="proto-check">
                        <Check size={11} aria-hidden />
                      </span>
                      <span>
                        {a.label}
                        <small>{a.meta}</small>
                      </span>
                    </button>
                  ))}
                  <div className="proto-action-detail proto-anim" aria-live="polite" style={at(T.detail)}>
                    <span>Selected execution receipt</span>
                    <p key={activeAction}>{AFTER_ACTIONS[activeAction].detail}</p>
                  </div>
                  <span className="proto-resolved proto-anim" style={at(T.resolved)}>
                    Resolved &middot; full record saved
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="proto-cta-row">
            <a className="btn-primary btn" href={`${SHOWCASE_BASE}/`} target="_blank" rel="noopener noreferrer">
              Explore the live prototypes <ArrowRight size={15} />
            </a>
            <span className="proto-cta-note">
              Illustrative example. Outcomes depend on policy terms and your own approval thresholds.
            </span>
          </div>
        </div>
      </section>

      <style>{`
        .proto-fx{--proto-mono:'IBM Plex Mono',ui-monospace,monospace;--proto-ease:cubic-bezier(.2,.7,.1,1)}
        .proto-fx .container{max-width:1160px;margin:0 auto;padding:0 32px}
        .proto-fx h2 em{font-style:normal;color:hsl(var(--accent))}

        /* spine */
        .proto-fx .proto-spine{display:flex;align-items:center;gap:16px;margin-bottom:18px}
        .proto-fx .proto-spine-label{color:hsl(var(--muted));font:11px var(--proto-mono);letter-spacing:.06em;text-transform:uppercase}
        .proto-fx .proto-spine-line{flex:1;height:1px;transform-origin:left;background:repeating-linear-gradient(90deg,hsl(var(--border)) 0 6px,transparent 6px 12px)}
        .proto-fx .proto-replay{display:inline-flex;align-items:center;gap:7px;min-height:28px;padding:0 10px;border:1px solid hsl(var(--border));border-radius:1px;background:transparent;color:hsl(var(--muted));font:10px/1 var(--proto-mono);letter-spacing:.08em;text-transform:uppercase;cursor:pointer;transition:color .2s ease,border-color .2s ease}
        .proto-fx .proto-replay:hover{border-color:hsl(var(--accent)/.6);color:hsl(var(--accent))}
        .proto-fx .proto-replay:focus-visible,.proto-fx .proto-act:focus-visible{outline:2px solid hsl(var(--accent));outline-offset:2px}

        /* before / after */
        .proto-fx .proto-ba{display:grid;grid-template-columns:1fr 52px 1fr;align-items:stretch}
        .proto-fx .proto-col{position:relative;display:flex;flex-direction:column;padding:24px 22px;border:1px solid hsl(var(--border));background:hsl(var(--panel))}
        .proto-fx .proto-col.before{background:hsl(var(--bg)/.4)}
        .proto-fx .proto-col.after{border-color:hsl(var(--accent)/.55)}
        .proto-fx .proto-col.after::before{content:"";position:absolute;top:-1px;bottom:-1px;left:-1px;width:3px;transform-origin:top;background:hsl(var(--accent))}
        .proto-fx .proto-col-tag{margin-bottom:16px;color:hsl(var(--muted));font:600 11px var(--proto-mono);letter-spacing:.06em;text-transform:uppercase}
        .proto-fx .proto-col-tag.good{color:hsl(var(--accent))}
        .proto-fx .proto-row{position:relative;display:flex;align-items:flex-end;gap:10px;margin-bottom:12px}
        .proto-fx .proto-row.mine{flex-direction:row-reverse}
        .proto-fx .proto-av{display:flex;flex:0 0 auto;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;background:hsl(var(--muted));color:hsl(var(--bg));font:700 10px var(--proto-mono)}
        .proto-fx .proto-av.on{background:hsl(var(--accent));color:hsl(var(--accent-foreground))}
        .proto-fx .proto-bubble{max-width:80%;padding:10px 14px;border:1px solid hsl(var(--border));background:hsl(var(--panel));color:hsl(var(--text));font-size:14px;line-height:1.45}
        .proto-fx .proto-col.before .proto-row.mine .proto-bubble{border-color:hsl(var(--muted)/.2);background:hsl(var(--muted)/.14);color:hsl(var(--muted))}
        .proto-fx .proto-bubble.on{border-color:hsl(var(--accent));background:hsl(var(--accent));color:hsl(var(--accent-foreground))}
        .proto-fx .proto-typing{position:absolute;right:38px;bottom:0;display:flex;gap:4px;max-width:none;padding:13px 14px;opacity:0;pointer-events:none}
        .proto-fx .proto-typing i{width:5px;height:5px;border-radius:50%;background:hsl(var(--muted))}
        .proto-fx .proto-deadend{display:flex;align-items:center;gap:8px;margin:8px 0 0 38px;color:hsl(var(--muted));font:12.5px var(--proto-mono)}
        .proto-fx .proto-dots{letter-spacing:2px}
        .proto-fx .proto-mid{display:flex;align-items:center;justify-content:center}
        .proto-fx .proto-arrow{position:relative;display:flex;align-items:center;justify-content:center;width:38px;height:38px;border-radius:50%;background:hsl(var(--accent));color:hsl(var(--accent-foreground));box-shadow:0 8px 20px -8px hsl(var(--accent)/.6)}
        .proto-fx .proto-arrow::after{content:"";position:absolute;inset:0;border:1px solid hsl(var(--accent));border-radius:50%;opacity:0}
        .proto-fx .proto-actions{display:flex;flex-direction:column;gap:9px;margin-top:auto;padding-top:16px;border-top:1px dashed hsl(var(--border))}
        .proto-fx .proto-act{display:flex;align-items:center;gap:12px;width:100%;padding:9px 10px;border:1px solid transparent;background:transparent;color:hsl(var(--text));font:inherit;font-size:13px;text-align:left;cursor:pointer;transition:border-color .18s ease,background .18s ease,transform .18s ease}
        .proto-fx .proto-act:hover,.proto-fx .proto-act.active{border-color:hsl(var(--border));background:hsl(var(--accent)/.07)}
        .proto-fx .proto-act:hover{transform:translateX(2px)}
        .proto-fx .proto-act small{display:block;margin-top:3px;color:hsl(var(--muted));font:10px/1.2 var(--proto-mono);letter-spacing:.08em}
        .proto-fx .proto-check{position:relative;display:flex;flex:0 0 auto;align-items:center;justify-content:center;width:17px;height:17px;border:1px solid hsl(var(--accent));background:hsl(var(--accent));color:hsl(var(--accent-foreground))}
        .proto-fx .proto-check::after{content:"";position:absolute;inset:-5px;border:1.5px solid transparent;border-top-color:hsl(var(--accent));border-radius:50%;opacity:0}
        .proto-fx .proto-action-detail{margin-top:4px;padding:13px 14px;border-left:2px solid hsl(var(--accent));background:hsl(var(--text)/.035)}
        .proto-fx .proto-action-detail span{color:hsl(var(--accent));font:10px/1 var(--proto-mono);letter-spacing:.1em;text-transform:uppercase}
        .proto-fx .proto-action-detail p{margin:7px 0 0;color:hsl(var(--muted));font-size:12.5px;line-height:1.5}
        .proto-fx .proto-resolved{display:inline-flex;align-items:center;align-self:flex-start;margin-top:6px;padding:6px 12px;background:hsl(var(--accent));color:hsl(var(--accent-foreground));font:600 11.5px var(--proto-mono);letter-spacing:.03em;text-transform:uppercase}
        .proto-fx .proto-cta-row{display:flex;flex-wrap:wrap;align-items:center;gap:18px;margin-top:24px}
        .proto-fx .proto-cta-note{max-width:460px;color:hsl(var(--muted));font-size:12.5px}

        /* Playback. Armed = hidden, seen = plays, idle / reduced motion = finished state. */
        .proto-fx [data-reveal="armed"] .proto-anim,.proto-fx [data-reveal="armed"] .proto-arrow{opacity:0}
        .proto-fx [data-reveal="armed"] .proto-spine-line{transform:scaleX(0)}
        .proto-fx [data-reveal="armed"] .proto-col.after::before{transform:scaleY(0)}
        @media (prefers-reduced-motion:no-preference){
          .proto-fx [data-reveal="seen"] .proto-spine-line{animation:proto-draw 1.1s var(--proto-ease) both}
          .proto-fx [data-reveal="seen"] .proto-anim{animation:proto-in .5s var(--proto-ease) var(--d,0s) both}
          .proto-fx [data-reveal="seen"] .proto-col.after::before{animation:proto-bar .7s var(--proto-ease) var(--d,0s) both}
          .proto-fx [data-reveal="seen"] .proto-typing{animation:proto-typing .9s ease var(--d) both}
          .proto-fx [data-reveal="seen"] .proto-typing i{animation:proto-dot 1s ease-in-out var(--d) 1 both}
          .proto-fx [data-reveal="seen"] .proto-typing i:nth-child(2){animation-delay:calc(var(--d) + .12s)}
          .proto-fx [data-reveal="seen"] .proto-typing i:nth-child(3){animation-delay:calc(var(--d) + .24s)}
          .proto-fx [data-reveal="seen"] .proto-dots{animation:proto-blink 1.6s steps(2,jump-none) calc(var(--d,2.4s)) infinite}
          .proto-fx [data-reveal="seen"] .proto-arrow{animation:proto-pop .5s var(--proto-ease) var(--d) both}
          .proto-fx [data-reveal="seen"] .proto-arrow::after{animation:proto-ripple 1s ease-out calc(var(--d) + .3s) 1 both}
          .proto-fx [data-reveal="seen"] .proto-check{animation:proto-fill .3s var(--proto-ease) calc(var(--d) + .7s) both}
          .proto-fx [data-reveal="seen"] .proto-check::after{animation:proto-ring .7s linear var(--d) 1 both}
          .proto-fx [data-reveal="seen"] .proto-resolved{animation:proto-stamp .45s var(--proto-ease) var(--d) both}
          .proto-fx .proto-action-detail p{animation:proto-swap .35s var(--proto-ease) both}
          @keyframes proto-draw{from{transform:scaleX(0)}to{transform:scaleX(1)}}
          @keyframes proto-in{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
          @keyframes proto-bar{from{transform:scaleY(0)}to{transform:scaleY(1)}}
          @keyframes proto-typing{0%{opacity:0;transform:translateY(6px)}12%,88%{opacity:1;transform:none}100%{opacity:0}}
          @keyframes proto-dot{0%,100%{transform:translateY(0);opacity:.45}40%{transform:translateY(-3px);opacity:1}}
          @keyframes proto-blink{0%{opacity:1}100%{opacity:.25}}
          @keyframes proto-pop{from{opacity:0;transform:scale(.5)}to{opacity:1;transform:none}}
          @keyframes proto-ripple{from{opacity:.8;transform:scale(1)}to{opacity:0;transform:scale(2.2)}}
          @keyframes proto-fill{from{background:transparent;color:transparent;transform:scale(.8)}to{background:hsl(var(--accent));color:hsl(var(--accent-foreground));transform:none}}
          @keyframes proto-ring{0%{opacity:1;transform:rotate(0)}99%{opacity:1;transform:rotate(360deg)}100%{opacity:0;transform:rotate(360deg)}}
          @keyframes proto-stamp{from{opacity:0;transform:scale(1.35) rotate(-3deg)}to{opacity:1;transform:none}}
          @keyframes proto-swap{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}
        }
        @media (prefers-reduced-motion:reduce){
          .proto-fx .proto-replay{display:none}
          .proto-fx .proto-act{transition:none}
        }
        @media (max-width:820px){
          .proto-fx .proto-ba{grid-template-columns:1fr}
          .proto-fx .proto-mid{padding:8px 0}
          .proto-fx .proto-arrow{transform:rotate(90deg)}
        }
        @media (max-width:600px){
          .proto-fx .container{padding:0 18px}
          .proto-fx .proto-col{padding:20px 16px}
          .proto-fx .proto-bubble{max-width:86%}
        }

        /* card art (theme-aware strokes/fills, depth-shadowed) */
        .proto-fx .proto-card-art svg{position:relative;z-index:1}
        .proto-fx .pa-panel{fill:hsl(var(--panel));stroke:hsl(var(--border));stroke-width:1.4}
        .proto-fx .pa-node{fill:hsl(var(--muted))}
        .proto-fx .pa-node-fill{fill:hsl(var(--panel));stroke:hsl(var(--muted));stroke-width:1.6}
        .proto-fx .pa-node-acc{fill:hsl(var(--accent))}
        .proto-fx .pa-accent-node{fill:hsl(var(--accent));stroke:hsl(var(--accent));stroke-width:2}
        .proto-fx .pa-line{stroke:hsl(var(--border));stroke-width:1.6;fill:none}
        .proto-fx .pa-line-fill{fill:hsl(var(--muted)/.32)}
        .proto-fx .pa-bubble-in{fill:hsl(var(--muted)/.22)}
        .proto-fx .pa-bubble-acc{fill:hsl(var(--accent))}
        .proto-fx .pa-accent{stroke:hsl(var(--accent));stroke-width:2.2;stroke-linecap:round;stroke-linejoin:round;fill:none}
        .proto-fx .pa-accent-dash{stroke:hsl(var(--accent));stroke-width:2;stroke-dasharray:4 4;fill:none}
        .proto-fx .pa-accent-fill{fill:hsl(var(--accent)/.1);stroke:hsl(var(--accent));stroke-width:1.5}
        .proto-fx .pa-accent-soft{fill:hsl(var(--accent)/.3)}
        .proto-fx .pa-good-fill{fill:hsl(var(--good)/.18);stroke:hsl(var(--good));stroke-width:1.6}
        .proto-fx .pa-check{stroke:hsl(var(--good));stroke-width:2.2;stroke-linecap:round;stroke-linejoin:round;fill:none}
        .proto-fx .pa-flag-edge{stroke:hsl(var(--warn));stroke-width:2.4;fill:none}
        .proto-fx .pa-flag-fill{fill:hsl(var(--warn));stroke:hsl(var(--warn));stroke-width:2}
        .proto-fx .pa-flag-mark{stroke:hsl(var(--bg));stroke-width:2;stroke-linecap:round}
        .proto-fx .pa-flag-dot{fill:hsl(var(--bg))}
        .proto-fx .pa-ring{fill:none;stroke:hsl(var(--warn));stroke-width:1.6;opacity:.55;stroke-dasharray:2 3}
        @media (prefers-reduced-motion:no-preference){
          .proto-fx .pa-accent-dash{animation:pa-march 1.2s linear infinite}
          .proto-fx .pa-ring{transform-box:fill-box;transform-origin:center;animation:pa-spin 14s linear infinite}
          @keyframes pa-march{to{stroke-dashoffset:-16}}
          @keyframes pa-spin{to{transform:rotate(360deg)}}
        }
      `}</style>
    </>
  );
}